import { useState, useEffect, Component, Suspense, lazy } from 'react';
import { supabase } from './supabaseClient';
const Landing = lazy(() => import('./Landing'));
const Login = lazy(() => import('./Login'));
const Pricing = lazy(() => import('./Pricing'));
const Success = lazy(() => import('./Success'));
const Agent = lazy(() => import('./Agent'));
const Admin = lazy(() => import('./Admin'));
const BlogRPM = lazy(() => import('./BlogRPM'));
const BlogOptimization = lazy(() => import('./BlogOptimization'));
const BlogCPC = lazy(() => import('./BlogCPC'));
const BlogCTR = lazy(() => import('./BlogCTR'));
const BlogAdLimit = lazy(() => import('./BlogAdLimit'));
const BlogRevenueDrop = lazy(() => import('./BlogRevenueDrop'));
const Blog = lazy(() => import('./Blog'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const Changelog = lazy(() => import('./Changelog'));
const CookieConsent = lazy(() => import('./CookieConsent'));

// Module-level guards: prevent double welcome email when both getSession() and
// onAuthStateChange() call loadProfile() near-simultaneously on page load.
// Unlike sessionStorage, let variables are guaranteed synchronous across calls.
let welcomeSending = false;
let loadProfileLock = false;

// ── Error Boundary — catches any unhandled crash and shows a recovery screen ──
// Without this, a single JS error anywhere in the app shows a blank white page.
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App crash caught by ErrorBoundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'system-ui,sans-serif' }}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: '40px 32px', maxWidth: 480, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', marginBottom: 12 }}>⚠️</div>
            <h2 style={{ color: '#1e293b', fontWeight: 900, marginBottom: 8 }}>Something went wrong</h2>
            <p style={{ color: '#64748b', fontSize: '0.88em', lineHeight: 1.7, marginBottom: 24 }}>
              An unexpected error occurred. Your data is safe — please refresh to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{ background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', border: 'none', borderRadius: 10, padding: '12px 32px', fontWeight: 800, fontSize: '0.9em', cursor: 'pointer', color: '#1a0a00' }}>
              Refresh Page
            </button>
            {process.env.NODE_ENV === 'development' && (
              <pre style={{ marginTop: 20, background: '#f8fafc', borderRadius: 8, padding: 12, fontSize: '0.65em', color: '#f43f5e', textAlign: 'left', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                {this.state.error?.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Main App ──────────────────────────────────────────────────────────────────
function AppInner() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [page, setPage] = useState('home');
  const [checking, setChecking] = useState(true);
  const [paywallLoading, setPaywallLoading] = useState(null);
  const [paywallError, setPaywallError] = useState('');
  const [sessionError, setSessionError] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // ── Map URL paths to page names ───────────────────────────────────────────
  const pathToPage = (path) => {
    if (path === '/blog') return 'blog';
    if (path === '/blog/increase-adsense-rpm') return 'blog-rpm';
    if (path === '/blog/adsense-optimization-tips') return 'blog-optimization';
    if (path === '/blog/increase-adsense-cpc') return 'blog-cpc';
    if (path === '/blog/increase-adsense-ctr') return 'blog-ctr';
    if (path === '/blog/avoid-adsense-ad-limit') return 'blog-adlimit';
    if (path === '/blog/adsense-revenue-dropping') return 'blog-revenuedrop';
    if (path === '/privacy') return 'privacy';
    if (path === '/terms') return 'terms';
    if (path === '/changelog') return 'changelog';
    return 'home';
  };

  // ── Browser back button support ────────────────────────────────────────────
  useEffect(() => {
    const onPopState = () => {
      if (window.location.hash.includes('type=recovery')) return;
      const resolved = pathToPage(window.location.pathname);
      setPage(resolved);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Helper: navigate to a page with browser history support
  const navigateTo = (newPage) => {
    if (newPage === 'login' || newPage === 'paywall') {
      window.history.pushState({ page: newPage }, '', window.location.pathname);
    }
    setPage(newPage);
  };

  useEffect(() => {
    // ── Check for password recovery FIRST before anything else ──────────────
    const hash = window.location.hash;
    if (hash.includes('type=recovery') || hash.includes('type=email_change')) {
      setPage('login');
      setChecking(false);
      return;
    }

    if (window.location.pathname === '/success') setPage('success');
    if (window.location.pathname === '/privacy') setPage('privacy');
    if (window.location.pathname === '/terms') setPage('terms');
    if (window.location.pathname === '/admin') setPage('admin');
    if (window.location.pathname === '/changelog') setPage('changelog');
    if (window.location.pathname === '/blog') setPage('blog');
    if (window.location.pathname === '/blog/increase-adsense-rpm') setPage('blog-rpm');
    if (window.location.pathname === '/blog/adsense-optimization-tips') setPage('blog-optimization');
    if (window.location.pathname === '/blog/increase-adsense-cpc') setPage('blog-cpc');
    if (window.location.pathname === '/blog/increase-adsense-ctr') setPage('blog-ctr');
    if (window.location.pathname === '/blog/avoid-adsense-ad-limit') setPage('blog-adlimit');
    if (window.location.pathname === '/blog/adsense-revenue-dropping') setPage('blog-revenuedrop');

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('getSession error:', error.message);
        setChecking(false);
        return;
      }
      setSession(session);
      if (session) {
        loadProfile(session.user.id);
        // Clean leftover OAuth hash (e.g. /#access_token=...)
        if (window.location.hash && !window.location.hash.includes('type=recovery')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }
      else setChecking(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setPage('login');
        setChecking(false);
        return;
      }

      // ── Session expiry: only redirect if user HAD a session ──────────────
      // SIGNED_OUT also fires on first load when there's simply no session.
      // We check the previous `session` state value — if it was set, this is
      // a real expiry. If not, it's just a cold start with no login — don't
      // redirect away from the home page.
      if (event === 'SIGNED_OUT') {
        if (session) {
          setSessionError('Your session has expired. Please log in again.');
          setPage('login');
        }
        setSession(null);
        setProfile(null);
        setChecking(false);
        return;
      }

      setSession(session);
      if (session) {
        loadProfile(session.user.id);
        // Fire-and-forget: track user IP on OAuth/token login
        fetch('/api/support?action=track-login', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        }).catch(() => { });
      }
      else { setProfile(null); setChecking(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadProfile = async (userId) => {
    // Prevent concurrent calls — getSession and onAuthStateChange can both
    // trigger this within milliseconds on email+password signup
    if (loadProfileLock) return;
    loadProfileLock = true;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('is_pro, subscription_status')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('loadProfile error:', error.message);
        // Clear stale session — profile doesn't exist (account may be deleted)
        await supabase.auth.signOut();
        setSession(null);
        setProfile(null);
        setPage('home');
        setChecking(false);
        return;
      }

      setProfile(data);
      setChecking(false);

      // Never override these pages — they handle their own flow
      const path = window.location.pathname;
      const protectedPages = ['/success', '/privacy', '/terms', '/admin', '/blog'];
      if (protectedPages.some(p => path.startsWith(p))) return;

      if (data?.is_pro) setPage('agent');
      else {
        setPage('paywall');
        // Send welcome email to unpaid users — module-level guard is truly synchronous
        // preventing the race between getSession and onAuthStateChange both calling loadProfile
        if (!welcomeSending) {
          welcomeSending = true;
          (async () => {
            try {
              const { data: { session: s } } = await supabase.auth.getSession();
              if (!s) return;
              const { data: p } = await supabase
                .from('profiles')
                .select('welcome_email_sent')
                .eq('id', s.user.id)
                .single();
              if (!p?.welcome_email_sent) {
                fetch('/api/emails?action=welcome', {
                  method: 'POST',
                  headers: { 'Authorization': `Bearer ${s.access_token}` },
                }).catch(() => { });
              }
            } catch (_) { }
          })();
        }
      }
    } catch (err) {
      loadProfileLock = false; // allow retry on error
      console.error('loadProfile exception:', err.message);
      setSessionError('Connection error. Please check your internet and try again.');
      setPage('login');
      setChecking(false);
    }
  };

  const handleLogin = (session) => {
    setSessionError('');
    setSession(session);
    loadProfile(session.user.id);
    // Fire-and-forget: track user IP on login
    fetch('/api/support?action=track-login', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${session.access_token}` },
    }).catch(() => { });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
    setPage('home');
    setSessionError('');
  };

  if (checking) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#f59e0b', fontSize: '1em', fontFamily: 'system-ui' }}>⏳ Loading…</div>
      </div>
    );
  }

  if (page === 'home') {
    return <><Landing onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  }

  if (page === 'privacy') return <><PrivacyPolicy /><CookieConsent /></>;
  if (page === 'terms') return <><TermsOfService /><CookieConsent /></>;

  if (page === 'admin') return <><Admin /><CookieConsent /></>;
  if (page === 'blog-rpm') return <><BlogRPM onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog-optimization') return <><BlogOptimization onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog-cpc') return <><BlogCPC onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog-ctr') return <><BlogCTR onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog-adlimit') return <><BlogAdLimit onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog-revenuedrop') return <><BlogRevenueDrop onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  if (page === 'blog') {
    const blogNav = (slug) => { window.history.pushState({}, '', slug); setPage(slug === '/blog/increase-adsense-rpm' ? 'blog-rpm' : slug === '/blog/adsense-optimization-tips' ? 'blog-optimization' : slug === '/blog/increase-adsense-cpc' ? 'blog-cpc' : slug === '/blog/increase-adsense-ctr' ? 'blog-ctr' : slug === '/blog/avoid-adsense-ad-limit' ? 'blog-adlimit' : slug === '/blog/adsense-revenue-dropping' ? 'blog-revenuedrop' : 'blog'); };
    return <><Blog onNavigate={blogNav} onGetStarted={() => navigateTo(session ? (profile?.is_pro ? 'agent' : 'paywall') : 'login')} /><CookieConsent /></>;
  }
  if (page === 'changelog') return <><Changelog /><CookieConsent /></>;

  if (page === 'login' || (!session && page !== 'home')) {
    return <Login onLogin={handleLogin} onBack={() => setPage('home')} sessionError={sessionError} />;
  }

  if (page === 'success') {
    return <Success onContinue={async () => {
      window.history.pushState({}, '', '/');
      // Force agent page first — user already confirmed payment
      setPage('agent');
      // Then reload profile in background to get fresh plan data
      // We use a direct Supabase query to avoid loadProfile overriding the page
      try {
        const { data } = await supabase.from('profiles').select('is_pro, subscription_status').eq('id', session.user.id).single();
        if (data) setProfile(data);
        // Only redirect to paywall if truly not activated after 3 more seconds
        if (!data?.is_pro) {
          await new Promise(r => setTimeout(r, 3000));
          const { data: data2 } = await supabase.from('profiles').select('is_pro, subscription_status').eq('id', session.user.id).single();
          if (data2) { setProfile(data2); if (data2.is_pro) setPage('agent'); else setPage('paywall'); }
        }
      } catch (_) { }
    }} />;
  }

  if (page === 'pricing') {
    return <Pricing session={session} onBack={() => setPage(profile?.is_pro ? 'agent' : 'paywall')} />;
  }

  if (!profile?.is_pro) {
    const paywallPlans = [
      {
        id: 'basic',
        name: 'Basic',
        price: '$20',
        desc: 'Perfect for bloggers & individual publishers. Cancel anytime.',
        searches: 15,
        priceId: process.env.REACT_APP_STRIPE_BASIC_PRICE_ID,
        features: ['Ad Codes (15 Templates)', 'AdSense Reports Analysis', 'Site Revenue Analysis', 'Full SEO Audit', 'Health Scoring System (0\u2013100)', 'Team Support'],
        badge: null,
      },
      {
        id: 'pro',
        name: 'Pro',
        price: '$39',
        desc: 'For serious publishers managing multiple sites. Cancel anytime.',
        searches: 60,
        priceId: process.env.REACT_APP_STRIPE_PRO_PRICE_ID,
        features: ['Everything in Basic', 'Ad Manager Data Analysis', 'Policy Checker', 'Site Optimiser & Auto Article Generation', 'Live Website Data Optimisation', 'Visual Ad Placement Map', 'Keywords Discovery', '30-Day Revenue Roadmap', 'Ad Limit Risk Analysis', 'Competitor Intelligence', 'Revenue Engine Optimisation'],
        badge: '🔥 MOST POPULAR',
      },
    ];

    const handlePaywallCheckout = async (plan) => {
      setPaywallLoading(plan.id);
      setPaywallError('');
      try {
        const { data: { session: authSession } } = await supabase.auth.getSession();
        if (!authSession) {
          setPaywallError('Session expired. Please log in again.');
          setPage('login');
          return;
        }
        const res = await fetch('/api/create-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authSession.access_token}`,
          },
          body: JSON.stringify({ mode: 'subscription', priceId: plan.priceId }),
        });
        const data = await res.json();
        if (data.url) window.location.href = data.url;
        else setPaywallError(data.error || 'Something went wrong. Please try again.');
      } catch (err) {
        setPaywallError('Connection error. Please try again.');
      }
      setPaywallLoading(null);
    };

    return (
      <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: 'system-ui,sans-serif', padding: isMobile ? '24px 16px' : '40px 20px' }}>
        <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap');@keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 860, margin: '0 auto 28px', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontFamily: 'Inter,sans-serif', fontWeight: 900, fontSize: '1.1em', color: '#d97706' }}>💰 AdSense Maximizer</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {!isMobile && <span style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.78em', color: '#94a3b8' }}>{session.user.email}</span>}
            <button onClick={handleLogout} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 7, color: '#94a3b8', padding: '6px 14px', fontSize: '0.75em', cursor: 'pointer', fontFamily: 'Inter,sans-serif' }}>Log out</button>
          </div>
        </div>

        <div style={{ maxWidth: 860, margin: '0 auto', animation: 'fadeUp 0.5s ease' }}>
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <h1 style={{ fontFamily: 'Inter,sans-serif', fontWeight: 900, fontSize: 'clamp(1.6em,4vw,2.4em)', color: '#1e293b', margin: '0 0 10px', lineHeight: 1.2 }}>
              Choose Your Plan
            </h1>
            <p style={{ fontFamily: 'Inter,sans-serif', color: '#64748b', fontSize: '0.92em', lineHeight: 1.7 }}>
              All plans include every AI tool. Resets every 30 days. Cancel anytime.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
            {paywallPlans.map((plan) => (
              <div key={plan.id} style={{
                background: plan.badge ? 'linear-gradient(145deg,#1e293b,#0f172a)' : '#ffffff',
                border: `2px solid ${plan.badge ? '#f59e0b' : '#e2e8f0'}`,
                borderRadius: 18, padding: isMobile ? '24px 20px' : 32, position: 'relative',
                boxShadow: plan.badge ? '0 8px 32px #f59e0b1a' : '0 2px 12px #0000000a',
              }}>
                {plan.badge && (
                  <div style={{ position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(135deg,#f59e0b,#fbbf24)', color: '#1a0a00', fontSize: '0.65em', fontFamily: 'Inter,sans-serif', fontWeight: 800, padding: '5px 18px', borderRadius: 99, whiteSpace: 'nowrap' }}>
                    {plan.badge}
                  </div>
                )}
                <div style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.68em', fontWeight: 700, color: plan.badge ? '#f59e0b' : '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>{plan.name}</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginBottom: 6 }}>
                  <span style={{ fontFamily: 'Inter,sans-serif', fontSize: '3em', fontWeight: 900, color: plan.badge ? '#fbbf24' : '#1e293b', lineHeight: 1 }}>{plan.price}</span>
                  <span style={{ fontFamily: 'Inter,sans-serif', color: '#94a3b8', marginBottom: 8 }}>/month</span>
                </div>
                <div style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.78em', color: plan.badge ? '#94a3b8' : '#64748b', marginBottom: 16, lineHeight: 1.6 }}>{plan.desc}</div>
                <div style={{ background: plan.badge ? '#f59e0b18' : '#f8fafc', border: `1px solid ${plan.badge ? '#f59e0b44' : '#e2e8f0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 20 }}>
                  <span style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, color: plan.badge ? '#fbbf24' : '#1e293b', fontSize: '0.9em' }}>🔍 {plan.searches} AI searches / month</span>
                </div>
                <div style={{ marginBottom: 24 }}>
                  {plan.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                      <span style={{ color: plan.badge ? '#f59e0b' : '#22c55e', fontWeight: 700, fontSize: '0.8em', flexShrink: 0 }}>✓</span>
                      <span style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.8em', color: plan.badge ? '#cbd5e1' : '#475569' }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => handlePaywallCheckout(plan)} disabled={paywallLoading === plan.id}
                  style={{ width: '100%', background: paywallLoading === plan.id ? '#e2e8f0' : plan.badge ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : 'transparent', border: plan.badge ? 'none' : '2px solid #1e293b', borderRadius: 10, padding: '13px 0', fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: '0.9em', color: paywallLoading === plan.id ? '#94a3b8' : plan.badge ? '#1a0a00' : '#1e293b', cursor: paywallLoading === plan.id ? 'wait' : 'pointer', transition: 'all 0.2s' }}>
                  {paywallLoading === plan.id ? '⏳ Redirecting to Stripe...' : `Get ${plan.name} →`}
                </button>
              </div>
            ))}
          </div>

          {paywallError && (
            <div style={{ textAlign: 'center', color: '#f43f5e', fontFamily: 'Inter,sans-serif', fontSize: '0.82em', marginBottom: 16 }}>
              ⚠️ {paywallError}
            </div>
          )}

          <div style={{ textAlign: 'center', background: '#f0fdf4', border: '1px solid #22c55e33', borderRadius: 12, padding: '14px 20px' }}>
            <span style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.82em', color: '#16a34a', fontWeight: 700 }}>🛡️ 7-Day Money-Back Guarantee — </span>
            <span style={{ fontFamily: 'Inter,sans-serif', fontSize: '0.8em', color: '#64748b' }}>Not satisfied? Full refund, no questions asked.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Agent
      userEmail={session.user.email}
      userCreatedAt={session.user.created_at}
      onLogout={handleLogout}
      onManagePlan={() => setPage('pricing')}
    />
  );
}

// ── Wrap entire app in error boundary ────────────────────────────────────────
export default function App() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<div style={{ minHeight: '100vh', background: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ color: '#f59e0b', fontSize: '1em', fontFamily: 'system-ui' }}>⏳ Loading…</div></div>}>
        <AppInner />
      </Suspense>
    </ErrorBoundary>
  );
}
